/**
 * Formateo de la pantalla de Resumen: fechas, cantidades y antiguedad.
 *
 * Por que no se importa `screens/publication/format.tsx`, que ya tiene
 * `parseApiDate` y compania
 * ---------------------------------------------------------------------------
 * Por el bundle, no por gusto. `StoreOverviewScreen` es la ruta raiz y se
 * importa de forma ESTATICA desde `src/components/routes.tsx`; las otras
 * pantallas entran por `lazyScreen` y viven en su propio chunk. Importar el
 * modulo de publicacion desde aca arrastraria ese chunk --y su `api.ts`, y lo
 * que ese archivo importe manana-- al bundle inicial del panel. Son treinta
 * lineas repetidas contra el arranque de toda la aplicacion.
 *
 * Ademas evita el acoplamiento entre dos pantallas que escriben personas
 * distintas: si manana la de publicacion cambia como rotula sus fechas, esta no
 * se entera.
 */

/**
 * Fechas de la API: ISO 8601 **sin zona**.
 *
 * El backend guarda y devuelve UTC naive (`src/models/base.py::utc_now_naive`),
 * o sea cadenas como `2026-08-27T13:04:11.120`, sin `Z` ni offset. `new Date()`
 * las interpreta como hora LOCAL, asi que en Buenos Aires (UTC-3) todo se
 * mostraria tres horas en el futuro y un "hace 5 minutos" saldria negativo. Se
 * le agrega la `Z` cuando falta; si el backend algun dia manda offset, la
 * cadena ya lo trae y esta funcion no la toca.
 */
export function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const parsed = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const DATE_TIME = new Intl.DateTimeFormat("es-AR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Fecha y hora locales, o `--` si no hay dato. */
export function formatDateTime(value: string | null | undefined): string {
  const parsed = parseApiDate(value);
  return parsed === null ? "--" : DATE_TIME.format(parsed);
}

/**
 * "hace 4 min", "hace 8 meses". Cadena vacia si no hay dato.
 *
 * Llega hasta meses y anios a proposito: el dato mas importante de esta
 * pantalla es la antiguedad de la ultima sincronizacion, y "hace 255 d" obliga
 * a hacer una division mental para entender que hace mucho.
 */
export function formatRelative(value: string | null | undefined): string {
  const parsed = parseApiDate(value);
  if (parsed === null) return "";

  const seconds = Math.round((Date.now() - parsed.getTime()) / 1000);
  const abs = Math.abs(seconds);
  const suffix = (amount: number, unit: string) =>
    seconds >= 0 ? `hace ${amount} ${unit}` : `en ${amount} ${unit}`;

  if (abs < 60) return seconds >= 0 ? "recien" : "en instantes";
  if (abs < 3600) return suffix(Math.round(abs / 60), "min");
  if (abs < 86400) return suffix(Math.round(abs / 3600), "h");
  if (abs < 86400 * 45) return suffix(Math.round(abs / 86400), "d");
  if (abs < 86400 * 365) return suffix(Math.round(abs / (86400 * 30)), "meses");
  const years = Math.round((abs / (86400 * 365)) * 10) / 10;
  return suffix(years, years === 1 ? "anio" : "anios");
}

/** Dias transcurridos desde una fecha de la API, o `null` si no hay dato. */
export function daysSince(value: string | null | undefined): number | null {
  const parsed = parseApiDate(value);
  if (parsed === null) return null;
  return (Date.now() - parsed.getTime()) / 86_400_000;
}

const NUMBER = new Intl.NumberFormat("es-AR");

/** Cantidad con separador de miles. */
export function formatCount(value: number): string {
  return NUMBER.format(value);
}

/**
 * Porcentaje de `part` sobre `total`, redondeado a entero.
 *
 * Devuelve `null` cuando el total es 0: un "0%" sobre cero productos no es un
 * cero, es una division indefinida, y mostrarlo como porcentaje seria inventar
 * una medicion.
 */
export function percentOf(part: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.round((part / total) * 100);
}
